<template>
  <SimpleTable card-wrap data-cy="phone-list-card">
    <thead>
      <tr>
        <th>
          <div class="d-flex align-items-center">
            <i class="fa-regular fa-phone mr-2"></i>
            <span class="info-header text-sm font-bold mr-2">Phone</span>
            <div class="number-badge">{{ phones.length }}</div>
            <ExpandButton
              v-if="alternatePhonesExist"
              class="sera-ml-auto"
              title=""
              :expanded="showAdditionalPhones"
              @toggle-expanded="toggleShowAdditionalPhone"
            />
          </div>
        </th>
      </tr>
    </thead>
    <template v-for="(phone, i) in phones">
      <tr
        v-if="i === 0"
        :key="`${phone.id}-primary-header`"
        class="list-item-header font-bold"
      >
        <td><TruncateText>Primary Phone</TruncateText></td>
      </tr>
      <tr
        v-if="i === 0 && !phone.primary"
        :key="`${phone.id}-missing-primary-placeholder`"
        class="list-item"
      >
        <td>--</td>
      </tr>
      <tr
        v-if="
          showAdditionalItemHeader(showAdditionalPhones, primaryPhoneExists, i)
        "
        :key="`${phone.id}-additional-header`"
        class="list-item-header font-bold"
      >
        <td><TruncateText>Additional Phone(s)</TruncateText></td>
      </tr>
      <tr
        class="list-item"
        :class="{
          'list-item-collapsed': !phone.primary && !showAdditionalPhones
        }"
      >
        <td class="info">
          <div class="d-flex align-items-baseline gap-2">
            <TruncateText>
              {{ phone.phone_number }}
            </TruncateText>
            <i :class="`far fa-fw fa-${getPhoneIcon(phone)}`" />
          </div>
          <TruncateText class="label-text">
            {{ phone.label }}
          </TruncateText>
        </td>
      </tr>
    </template>

    <tr v-if="!phones.length">
      <td
        class="text-danger cursor-pointer !whitespace-nowrap"
        @click="emit('edit-profile')"
      >
        <i class="fas fa-exclamation-circle" />
        Missing Phone Number
      </td>
    </tr>
  </SimpleTable>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import ExpandButton from "@/components/ExpandButton.vue";
import SimpleTable from "@/components/SimpleTable.vue";
import TruncateText from "@/components/TruncateText.vue";
import { CustomerPhonesEntity } from "@/typings/customer";

const emit = defineEmits(["edit-profile"]);
const props = withDefaults(
  defineProps<{
    phones: CustomerPhonesEntity[];
  }>(),
  {
    phones: () => []
  }
);
const showAdditionalPhones = ref(false);
const primaryPhoneExists = computed(() =>
  props.phones.some((phone) => phone.primary)
);

const alternatePhonesExist = computed(() =>
  props.phones.some((phone) => !phone.primary)
);

const toggleShowAdditionalPhone = () => {
  showAdditionalPhones.value = !showAdditionalPhones.value;
};

const showAdditionalItemHeader = (toggleShow, primaryExists, index) => {
  if (!toggleShow) return false;

  switch (index) {
    case 0:
      return !primaryExists;
    case 1:
      return primaryExists;
    default:
      return false;
  }
};

const getPhoneIcon = (phone) => {
  switch (phone.type) {
    case "mobile":
      return "mobile";
    case "home":
      return "house-chimney";
    default:
      return "phone";
  }
};
</script>

<style lang="scss" scoped>
.label-text {
  white-space: nowrap;
  color: var(--sera-color-core-blue-cobalt-blue-800);
}

.list-item {
  td {
    white-space: nowrap;
  }

  &.list-item-collapsed {
    visibility: collapse;
  }
}

.info {
  color: #5d5d5d;
}

.info-header {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.info-icon {
  width: 50px;
  text-align: center;
}

.number-badge {
  display: flex;
  height: 18px;
  padding: 0 4px;
  line-height: 18px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid var(--sera-color-neutral-gray-med-gray-500);
  background: var(--sera-color-neutral-gray-light-gray-100);
  font-size: 12px;
  font-weight: 600;
}
</style>
